import type { LabResult } from '@/components/LabResultsTable';

/** LOINC code → panel. Moved here from LabResultsTable so both views share it. */
export const LAB_PANELS: Record<string, string[]> = {
  CBC: ['6690-2', '718-7', '4544-3', '777-3', '770-8', '731-0', '789-8', '787-2', '786-4', '785-6', '751-8'],
  Chemistry: ['2951-2', '2823-3', '2075-0', '2028-9', '3094-0', '2160-0', '2345-7'],
  'Blood Gas': ['2744-1', '2019-8', '2703-7', '1960-4'],
  Inflammatory: ['1988-5', '33959-8', '2524-7'],
  Viral: ['40988-8'],
  Liver: ['1920-8', '1742-6', '1975-2', '1751-7'],
  Coagulation: ['5902-2', '14979-9', '6301-6', '3255-7'],
};

export const LOINC_TO_PANEL: Record<string, string> = {};
Object.entries(LAB_PANELS).forEach(([panel, codes]) => codes.forEach((code) => { LOINC_TO_PANEL[code] = panel; }));

export const PANEL_ORDER = ['CBC', 'Chemistry', 'Blood Gas', 'Inflammatory', 'Viral', 'Liver', 'Coagulation', 'Other'];

export const PANEL_LABEL_KEYS: Record<string, string> = {
  CBC: 'records.panelCbc',
  Chemistry: 'records.panelChemistry',
  'Blood Gas': 'records.panelBloodGas',
  Inflammatory: 'records.panelInflammation',
  Viral: 'records.panelViral',
  Liver: 'records.panelLiver',
  Coagulation: 'records.panelCoagulation',
  Other: 'records.panelOther',
};

/**
 * Exact-name aliases, copied verbatim from each floor panel's `names` array in
 * server/config/watched-labs.ts (WATCHED_LAB_FLOOR), checked before the regex
 * fallbacks below. Some floor names (e.g. "Na", "K") are too short/ambiguous
 * for a safe regex, so they must classify by exact match. The
 * "keeps getLabPanel in sync with the server's watched-lab floor" test in
 * lab-panels.test.ts fails if this list drifts from the server floor — update
 * both together.
 */
const EXACT_NAME_PANELS: Record<string, string> = {};
const registerExactNames = (panel: string, names: string[]) => {
  names.forEach((name) => { EXACT_NAME_PANELS[name.trim().toLowerCase()] = panel; });
};
registerExactNames('CBC', [
  'White blood cells', 'WBC', 'White Blood Cell Count', 'Leukocytes',
  'Absolute neutrophils', 'ANC', 'Absolute Neutrophil Count', 'Neutrophils Absolute',
  'Hemoglobin', 'Hgb',
  'Platelets', 'Platelet Count',
]);
registerExactNames('Inflammatory', ['CRP', 'C-Reactive Protein', 'Procalcitonin']);
registerExactNames('Chemistry', ['Sodium', 'Na', 'Potassium', 'K', 'Glucose', 'Creatinine']);
registerExactNames('Viral', ['RSV', 'RSV PCR', 'Respiratory Syncytial Virus']);

/** Regex fallbacks for names outside the server floor's exact-name list above. */
const NAME_PANELS: Array<[RegExp, string]> = [
  [/\b(wbc|rbc|hemoglobin|hgb|hematocrit|platelet|neutrophil|anc|lymphocyte|mcv|mch|rdw)\b/i, 'CBC'],
  [/\b(sodium|potassium|chloride|bicarb|co2|bun|creatinine|glucose|calcium|magnesium|phosph)\b/i, 'Chemistry'],
  [/\b(ph|pco2|po2|lactate|base excess)\b/i, 'Blood Gas'],
  [/\b(crp|c-reactive|procalcitonin|esr|sed rate)\b/i, 'Inflammatory'],
  [/\b(rsv|influenza|flu|covid|sars|rhino|adeno|viral|pcr)\b/i, 'Viral'],
  [/\b(alt|ast|bilirubin|alk phos|alkaline|albumin)\b/i, 'Liver'],
  [/\b(pt|inr|ptt|fibrinogen)\b/i, 'Coagulation'],
];

export function getLabPanel(lab: { name: string; loincCode?: string }): string {
  if (lab.loincCode && LOINC_TO_PANEL[lab.loincCode]) return LOINC_TO_PANEL[lab.loincCode];
  const exact = EXACT_NAME_PANELS[lab.name.trim().toLowerCase()];
  if (exact) return exact;
  const hit = NAME_PANELS.find(([re]) => re.test(lab.name));
  return hit ? hit[1] : 'Other';
}

export interface LabSeries {
  key: string;
  name: string;
  latest: LabResult;
  /** Oldest → newest. */
  results: LabResult[];
  /** The parseable values among `results`, in the same relative order; a non-numeric result (e.g. "<0.5") is simply omitted rather than blanking the whole series. */
  numeric: number[];
}

const time = (d: string): number => { const t = Date.parse(d); return Number.isNaN(t) ? 0 : t; };
/** A leading comparator ("<0.5", ">100") is a censored/inequality result, not a hard number — plotting it as its face value would misrepresent the reading, so it is treated as non-numeric here (still shown as text in the dated trend list). */
const asNumber = (v: string | number): number | null => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const trimmed = v.trim();
  if (/^[<>≤≥]/.test(trimmed)) return null;
  const n = parseFloat(trimmed.replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
};

/** Same identity FHIR observations sometimes duplicate on refetch (mirrors LabResultsTable's dedup). */
const dedupeLabs = (labs: LabResult[]): LabResult[] => {
  const seen = new Set<string>();
  return labs.filter((lab) => {
    const key = `${lab.name.toLowerCase()}|${lab.date}|${String(lab.value)}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export function groupLabsByPanel(labs: LabResult[]): Array<{ panel: string; newestDate: string; tests: LabSeries[] }> {
  const byPanel = new Map<string, Map<string, LabSeries>>();
  const sorted = dedupeLabs(labs).sort((a, b) => time(a.date) - time(b.date) || a.name.localeCompare(b.name));
  for (const lab of sorted) {
    const panel = getLabPanel(lab);
    const key = lab.name.trim().toLowerCase();
    if (!byPanel.has(panel)) byPanel.set(panel, new Map());
    const tests = byPanel.get(panel)!;
    const series = tests.get(key) ?? { key, name: lab.name, latest: lab, results: [], numeric: [] };
    series.results.push(lab);
    series.latest = lab;
    tests.set(key, series);
  }
  return PANEL_ORDER
    .filter((panel) => byPanel.has(panel))
    .map((panel) => {
      const tests = [...byPanel.get(panel)!.values()].map((s) => {
        const numbers = s.results.map((r) => asNumber(r.value));
        return { ...s, numeric: numbers.filter((n): n is number => n !== null) };
      });
      tests.sort((a, b) => time(b.latest.date) - time(a.latest.date) || a.name.localeCompare(b.name));
      const newestDate = tests.reduce((d, t) => (time(t.latest.date) > time(d) ? t.latest.date : d), tests[0].latest.date);
      return { panel, newestDate, tests };
    });
}
